import type { z } from "zod";

import { ApiErrorSchema } from "@/lib/contract/index.ts";

/**
 * The REST client, for the little that is not on the sync socket: signing in,
 * signing out, and finding out who you are.
 *
 * Same-origin by design. In production Caddy serves the app and proxies /api;
 * in development Vite does the same. So there is no base URL to configure, the
 * session cookie travels without any CORS credentials dance, and a request
 * from a page served elsewhere simply fails.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions<T> {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  /** Validates the response; a mismatch is a contract bug and throws. */
  schema?: z.ZodType<T>;
  signal?: AbortSignal;
}

export async function api<T = void>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  const init: RequestInit = {
    method: options.method ?? "GET",
    credentials: "same-origin",
    headers,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
    headers["content-type"] = "application/json";
  }
  if (options.signal) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    throw new ApiError(0, "network", "Can't reach the server. Check your connection.");
  }

  if (!response.ok) {
    let code = "internal";
    let message = "Something went wrong. Please try again.";
    try {
      const parsed = ApiErrorSchema.safeParse(await response.json());
      if (parsed.success) {
        code = parsed.data.error.code;
        message = parsed.data.error.message;
      }
    } catch {
      // Not JSON (a proxy error page, say). Keep the generic message.
    }
    throw new ApiError(response.status, code, message);
  }

  if (response.status === 204 || !options.schema) return undefined as T;
  return options.schema.parse(await response.json());
}
